import React, { useState, useMemo } from "react";
import { trpc } from "../lib/trpc.js";
import {
  ScrollText, Loader2, AlertTriangle, RefreshCw,
  Filter, Search, ChevronLeft, ChevronRight, ChevronDown,
  Activity, Zap, ShieldAlert, Info, TrendingUp, TrendingDown,
  Pause, Play, Settings2, BarChart3, Clock, X, CheckCircle2,
  XCircle, SkipForward, LayoutGrid, List, Bot, Eye, EyeOff,
  ClipboardList, DollarSign, Globe, Target, ArrowUpRight, ArrowDownRight,
  AlertCircle, Layers,
} from "lucide-react";
import { BacklogView } from "../components/BacklogView.js";

// ─── Types & Config ──────────────────────────────────────────

interface LogsFilter {
  category?: string;
  agent?: string;
  severity?: string;
  result?: string;
  strategy_tag?: string;
  campaign_id?: string;
  search?: string;
  date_from?: string;
  date_to?: string;
  limit: number;
  offset: number;
}

type ViewMode = "list" | "kanban" | "backlog";

// Agent color map — synced with ActionLogger.AGENT_COLORS on backend
const agentColorMap: Record<string, { color: string; label: string; gradient: string }> = {
  megan:                  { color: "#F07D00", label: "Megan",               gradient: "from-orange-500/20 to-orange-600/5" },
  performance_optimizer:  { color: "#22C55E", label: "Performance Optimizer", gradient: "from-emerald-500/20 to-emerald-600/5" },
  meta_optimizer:         { color: "#A855F7", label: "Meta-Optimizer",       gradient: "from-purple-500/20 to-purple-600/5" },
  strategist:             { color: "#3B82F6", label: "Strategist",           gradient: "from-blue-500/20 to-blue-600/5" },
  publish_manager:        { color: "#EC4899", label: "Publish Manager",      gradient: "from-pink-500/20 to-pink-600/5" },
  ads_manager:            { color: "#F59E0B", label: "Ads Manager",          gradient: "from-amber-500/20 to-amber-600/5" },
  creatives:              { color: "#8B5CF6", label: "Creatives",            gradient: "from-violet-500/20 to-violet-600/5" },
  creative_analyst:       { color: "#D946EF", label: "Creative Analyst",     gradient: "from-fuchsia-500/20 to-fuchsia-600/5" },
  monitor:                { color: "#06B6D4", label: "Monitor",              gradient: "from-cyan-500/20 to-cyan-600/5" },
  funnel_builder:         { color: "#14B8A6", label: "Funnel Builder",       gradient: "from-teal-500/20 to-teal-600/5" },
  content_processor:      { color: "#64748B", label: "Content Processor",    gradient: "from-slate-500/20 to-slate-600/5" },
  critic:                 { color: "#EF4444", label: "Critic",               gradient: "from-red-500/20 to-red-600/5" },
  broadcast:              { color: "#10B981", label: "Broadcast",            gradient: "from-emerald-500/20 to-emerald-600/5" },
  campaign_operations:    { color: "#F97316", label: "Campaign Ops",         gradient: "from-orange-500/20 to-orange-600/5" },
  megan_developer:        { color: "#6366F1", label: "Megan Developer",      gradient: "from-indigo-500/20 to-indigo-600/5" },
  developer:              { color: "#818CF8", label: "Developer",            gradient: "from-indigo-400/20 to-indigo-500/5" },
  product_owner:          { color: "#F59E0B", label: "Product Owner",        gradient: "from-amber-500/20 to-amber-600/5" },
  performance_optimizer_agent: { color: "#22C55E", label: "Perf. Optimizer", gradient: "from-emerald-500/20 to-emerald-600/5" },
  megan_test:             { color: "#94A3B8", label: "Megan Test",           gradient: "from-slate-400/20 to-slate-500/5" },
};

const categoryConfig: Record<string, { label: string; color: string; bg: string; icon: typeof Activity }> = {
  campaign_mgmt:  { label: "Campanha",      color: "text-blue-400",    bg: "bg-blue-500/10",    icon: Play },
  budget_opt:     { label: "Orçamento",     color: "text-emerald-400", bg: "bg-emerald-500/10", icon: TrendingUp },
  adset_ctrl:     { label: "Conjunto",      color: "text-amber-400",   bg: "bg-amber-500/10",   icon: Pause },
  config_push:    { label: "Config",        color: "text-purple-400",  bg: "bg-purple-500/10",  icon: Settings2 },
  analysis:       { label: "Análise",       color: "text-cyan-400",    bg: "bg-cyan-500/10",    icon: BarChart3 },
  creative:       { label: "Criativo",      color: "text-pink-400",    bg: "bg-pink-500/10",    icon: Zap },
  strategy:       { label: "Estratégia",    color: "text-blue-400",    bg: "bg-blue-500/10",    icon: TrendingUp },
  publishing:     { label: "Publicação",    color: "text-pink-400",    bg: "bg-pink-500/10",    icon: Play },
  monitoring:     { label: "Monitoramento", color: "text-cyan-400",    bg: "bg-cyan-500/10",    icon: Activity },
  content:        { label: "Conteúdo",      color: "text-slate-400",   bg: "bg-slate-500/10",   icon: ScrollText },
  funnel:         { label: "Funnel",        color: "text-teal-400",    bg: "bg-teal-500/10",    icon: TrendingDown },
  broadcast:      { label: "Broadcast",     color: "text-emerald-400", bg: "bg-emerald-500/10", icon: Zap },
  orchestration:  { label: "Orquestração",  color: "text-orange-400",  bg: "bg-orange-500/10",  icon: Settings2 },
  optimization:   { label: "Otimização",    color: "text-purple-400",  bg: "bg-purple-500/10",  icon: TrendingUp },
  review:         { label: "Revisão",       color: "text-red-400",     bg: "bg-red-500/10",     icon: ShieldAlert },
  system:         { label: "Sistema",       color: "text-slate-400",   bg: "bg-slate-500/10",   icon: Settings2 },
};

const severityConfig: Record<string, { label: string; color: string; bg: string; border: string; dot: string }> = {
  info:     { label: "Info",     color: "text-blue-400",    bg: "bg-blue-500/10",    border: "border-blue-500/20",    dot: "bg-blue-400" },
  action:   { label: "Ação",     color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/20", dot: "bg-emerald-400" },
  warning:  { label: "Alerta",   color: "text-amber-400",   bg: "bg-amber-500/10",   border: "border-amber-500/20",  dot: "bg-amber-400" },
  critical: { label: "Crítico",  color: "text-red-400",     bg: "bg-red-500/10",     border: "border-red-500/20",    dot: "bg-red-400 animate-pulse" },
};

const actionLabels: Record<string, string> = {
  pause_adset: "Pausar conjunto",
  activate_adset: "Ativar conjunto",
  scale_up: "Aumentar orçamento",
  scale_up_budget: "Aumentar orçamento",
  scale_down: "Reduzir orçamento",
  scale_down_skipped: "Redução ignorada",
  stoploss: "Stoploss",
  floop: "Floop (sem resultado)",
  reactivate: "Reativar",
  update_scope_tag: "Atualizar scope tag",
  update_maturation: "Atualizar maturação",
  out_of_scope: "Fora do escopo",
  maturation_tolerance: "Tolerância (maturação)",
  agent_started: "Agente iniciou",
  agent_completed: "Agente concluiu",
  agent_failed: "Agente falhou",
  agent_blocked: "Agente bloqueado",
};

// ─── Utility Functions ──────────────────────────────────────

function getAgentConfig(agentId: string) {
  return agentColorMap[agentId] || { color: "#94A3B8", label: agentId, gradient: "from-slate-500/20 to-slate-600/5" };
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch {
    return iso;
  }
}

function relativeTime(iso: string): string {
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "agora";
    if (mins < 60) return `${mins}min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days}d`;
  } catch {
    return "";
  }
}

// ─── Badge Components ────────────────────────────────────────

function CategoryBadge({ category }: { category: string }) {
  const config = categoryConfig[category] || { label: category, color: "text-muted-foreground", bg: "bg-muted/30", icon: Activity };
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium ${config.color} ${config.bg}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
}

function SeverityBadge({ severity }: { severity: string }) {
  const config = severityConfig[severity] || { label: severity, color: "text-muted-foreground", bg: "bg-muted/30", border: "border-border", dot: "bg-muted-foreground" };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${config.color} ${config.bg} border ${config.border}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
      {config.label}
    </span>
  );
}

function ResultBadge({ result }: { result: string | null }) {
  if (!result) return null;
  const configs: Record<string, { icon: typeof CheckCircle2; color: string; bg: string }> = {
    success: { icon: CheckCircle2, color: "text-emerald-400", bg: "bg-emerald-500/10" },
    error:   { icon: XCircle,      color: "text-red-400",     bg: "bg-red-500/10" },
    skipped: { icon: SkipForward,  color: "text-muted-foreground", bg: "bg-muted/30" },
    running: { icon: Activity,     color: "text-blue-400",    bg: "bg-blue-500/10" },
    blocked: { icon: ShieldAlert,  color: "text-amber-400",   bg: "bg-amber-500/10" },
  };
  const config = configs[result] || configs.skipped;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${config.color} ${config.bg}`}>
      <Icon className="w-3 h-3" />
      {result}
    </span>
  );
}

function AgentBadge({ agent }: { agent: string }) {
  const config = getAgentConfig(agent);
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold border"
      style={{
        color: config.color,
        backgroundColor: `${config.color}15`,
        borderColor: `${config.color}30`,
      }}
    >
      <Bot className="w-3 h-3" />
      {config.label}
    </span>
  );
}

// ─── Stats Cards ─────────────────────────────────────────────

function StatsCards() {
  const { data, isLoading } = trpc.brain.getLogsStats.useQuery(
    { period_hours: 24 },
    { refetchInterval: 60_000, retry: false }
  );

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-card border border-border rounded-xl p-4 animate-pulse">
            <div className="h-4 w-20 bg-border rounded mb-2" />
            <div className="h-8 w-16 bg-border rounded" />
          </div>
        ))}
      </div>
    );
  }

  if (!data) return null;

  const agentCount = Object.keys(data.by_agent || {}).length;

  const cards = [
    { label: "Total (24h)", value: data.total, icon: ScrollText, color: "text-foreground", accent: "border-primary/30" },
    { label: "Ações Executadas", value: data.by_severity?.action || 0, icon: Zap, color: "text-emerald-400", accent: "border-emerald-500/30" },
    { label: "Agentes Ativos", value: agentCount, icon: Bot, color: "text-blue-400", accent: "border-blue-500/30" },
    { label: "Críticos", value: data.by_severity?.critical || 0, icon: AlertTriangle, color: "text-red-400", accent: "border-red-500/30" },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <div key={card.label} className={`bg-card border-l-[3px] ${card.accent} border border-border rounded-xl p-4 transition-all duration-200 hover:shadow-lg hover:shadow-black/5`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider">{card.label}</span>
              <Icon className={`w-4 h-4 ${card.color}`} />
            </div>
            <p className={`text-3xl font-bold tracking-tight ${card.color}`}>{card.value}</p>
          </div>
        );
      })}
    </div>
  );
}

// ─── Filter Bar ──────────────────────────────────────────────

interface FilterBarProps {
  filters: LogsFilter;
  onFilterChange: (f: Partial<LogsFilter>) => void;
  onReset: () => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
}

function FilterBar({ filters, onFilterChange, onReset, viewMode, onViewModeChange }: FilterBarProps) {
  const [expanded, setExpanded] = useState(false);
  const hasFilters = filters.category || filters.severity || filters.agent || filters.result || filters.campaign_id || filters.search || filters.date_from || filters.date_to;

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      {/* Top bar: search + view toggle */}
      <div className="flex items-center gap-3 p-4 border-b border-border/50">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Buscar em ações e motivos..."
            value={filters.search || ""}
            onChange={(e) => onFilterChange({ search: e.target.value || undefined, offset: 0 })}
            className="w-full bg-background border border-border rounded-lg pl-10 pr-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 transition-all"
          />
        </div>

        {/* View toggle */}
        <div className="flex items-center bg-background border border-border rounded-lg p-0.5">
          <button
            onClick={() => onViewModeChange("list")}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all ${
              viewMode === "list"
                ? "bg-primary/15 text-primary shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <List className="w-3.5 h-3.5" />
            Lista
          </button>
          <button
            onClick={() => onViewModeChange("kanban")}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all ${
              viewMode === "kanban"
                ? "bg-primary/15 text-primary shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <LayoutGrid className="w-3.5 h-3.5" />
            Kanban
          </button>
          <button
            onClick={() => onViewModeChange("backlog")}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all ${
              viewMode === "backlog"
                ? "bg-primary/15 text-primary shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <ClipboardList className="w-3.5 h-3.5" />
            Backlog
          </button>
        </div>

        {/* Expand filters */}
        <button
          onClick={() => setExpanded(!expanded)}
          className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium rounded-lg border transition-all ${
            hasFilters
              ? "bg-primary/10 text-primary border-primary/30"
              : "bg-background text-muted-foreground border-border hover:text-foreground"
          }`}
        >
          <Filter className="w-3.5 h-3.5" />
          Filtros
          {hasFilters && <span className="w-1.5 h-1.5 rounded-full bg-primary" />}
          <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? "rotate-180" : ""}`} />
        </button>

        {hasFilters && (
          <button
            onClick={onReset}
            className="flex items-center gap-1 px-2.5 py-2.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Expanded filter row */}
      {expanded && (
        <div className="p-4 bg-background/30 animate-fade-in">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3">
            <select
              value={filters.category || ""}
              onChange={(e) => onFilterChange({ category: e.target.value || undefined, offset: 0 })}
              className="bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option value="">Categoria</option>
              {Object.entries(categoryConfig).map(([key, cfg]) => (
                <option key={key} value={key}>{cfg.label}</option>
              ))}
            </select>

            <select
              value={filters.severity || ""}
              onChange={(e) => onFilterChange({ severity: e.target.value || undefined, offset: 0 })}
              className="bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option value="">Severidade</option>
              {Object.entries(severityConfig).map(([key, cfg]) => (
                <option key={key} value={key}>{cfg.label}</option>
              ))}
            </select>

            <select
              value={filters.result || ""}
              onChange={(e) => onFilterChange({ result: e.target.value || undefined, offset: 0 })}
              className="bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option value="">Resultado</option>
              <option value="success">Success</option>
              <option value="error">Error</option>
              <option value="skipped">Skipped</option>
              <option value="running">Running</option>
              <option value="blocked">Blocked</option>
            </select>

            <input
              type="text"
              placeholder="Agente..."
              value={filters.agent || ""}
              onChange={(e) => onFilterChange({ agent: e.target.value || undefined, offset: 0 })}
              className="bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />

            <input
              type="text"
              placeholder="Campaign ID..."
              value={filters.campaign_id || ""}
              onChange={(e) => onFilterChange({ campaign_id: e.target.value || undefined, offset: 0 })}
              className="bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />

            <input
              type="date"
              value={filters.date_from || ""}
              onChange={(e) => onFilterChange({ date_from: e.target.value || undefined, offset: 0 })}
              className="bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />

            <input
              type="date"
              value={filters.date_to || ""}
              onChange={(e) => onFilterChange({ date_to: e.target.value || undefined, offset: 0 })}
              className="bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Rich Details Panel ────────────────────────────────────────────────────

const DETAIL_LABELS: Record<string, string> = {
  pipeline_id: "Pipeline ID",
  pipeline_name: "Pipeline",
  execution_id: "Execution ID",
  mode: "Modo",
  schedule: "Agendamento",
  run_count: "Execução #",
  workflow: "Workflow",
  steps_total: "Steps Total",
  steps_completed: "Steps Concluídos",
  steps_failed: "Steps Falharam",
  elapsed_seconds: "Duração",
  error: "Erro",
  tool_name: "Ferramenta",
  tool_args: "Argumentos",
  task: "Tarefa",
  original_persistent_id: "Task Original",
  new_task_id: "Nova Task",
  fallback_reason: "Motivo Fallback",
  business_summary: "Resumo de Negócio",
};

function formatDetailValue(key: string, value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (key === "elapsed_seconds" && typeof value === "number") {
    if (value < 60) return `${value.toFixed(1)}s`;
    return `${Math.floor(value / 60)}m ${Math.round(value % 60)}s`;
  }
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function DetailsPanelRich({
  details,
  agentColor,
  compact = false,
}: {
  details: Record<string, unknown>;
  agentColor: string;
  compact?: boolean;
}) {
  const stepSummaries = details.step_summaries as Record<string, { status: string; summary: string; data_preview?: Record<string, unknown> }> | undefined;
  const stepDetails = details.step_details as Record<string, { status: string; summary?: string; error?: string }> | undefined;
  const steps = stepSummaries || stepDetails;

  // Separate known fields from raw fields
  const knownKeys = new Set([
    ...Object.keys(DETAIL_LABELS),
    "step_summaries", "step_details", "step_detail",
  ]);
  const mainFields = Object.entries(details).filter(([k]) => knownKeys.has(k) && k !== "step_summaries" && k !== "step_details" && k !== "business_summary");
  const extraFields = Object.entries(details).filter(([k]) => !knownKeys.has(k));

  const textSize = compact ? "text-[10px]" : "text-[11px]";
  const labelSize = compact ? "text-[9px]" : "text-[10px]";

  return (
    <div className="space-y-2">
      {/* Main fields as grid */}
      {mainFields.length > 0 && (
        <div className={`grid ${compact ? "grid-cols-2 gap-2" : "grid-cols-2 md:grid-cols-3 gap-3"}`}>
          {mainFields.map(([key, value]) => (
            <div key={key}>
              <span className={`text-muted-foreground block mb-0.5 uppercase tracking-wider ${labelSize}`}>
                {DETAIL_LABELS[key] || key}
              </span>
              <span className={`text-foreground font-mono ${textSize} break-all`}>
                {formatDetailValue(key, value)}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Step summaries as timeline */}
      {steps && Object.keys(steps).length > 0 && (
        <div>
          <span className={`text-muted-foreground block mb-1.5 uppercase tracking-wider ${labelSize}`}>
            Etapas do Workflow
          </span>
          <div className="space-y-1">
            {Object.entries(steps).map(([stepId, step]) => {
              const isSuccess = step.status === "success" || step.status === "completed";
              const isFailed = step.status === "failed" || step.status === "error";
              const isSkipped = step.status === "skipped";
              return (
                <div
                  key={stepId}
                  className={`flex items-start gap-2 rounded-md px-2 py-1.5 border ${textSize}`}
                  style={{
                    backgroundColor: isSuccess ? `${agentColor}08` : isFailed ? "rgba(239,68,68,0.06)" : "rgba(128,128,128,0.04)",
                    borderColor: isSuccess ? `${agentColor}20` : isFailed ? "rgba(239,68,68,0.15)" : "rgba(128,128,128,0.1)",
                  }}
                >
                  <span className="shrink-0 mt-0.5">
                    {isSuccess && <CheckCircle2 className="w-3 h-3 text-green-500" />}
                    {isFailed && <XCircle className="w-3 h-3 text-red-500" />}
                    {isSkipped && <SkipForward className="w-3 h-3 text-muted-foreground" />}
                    {!isSuccess && !isFailed && !isSkipped && <Activity className="w-3 h-3 text-yellow-500" />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <span className="font-semibold text-foreground">Step {stepId}</span>
                    <span className="text-muted-foreground ml-1.5">— {step.status}</span>
                    {(step.summary || (step as any).error) && (
                      <p className="text-muted-foreground mt-0.5 break-words">
                        {step.summary || (step as any).error}
                      </p>
                    )}
                    {/* Render data_preview as business metrics */}
                    {(step as any).data_preview && Object.keys((step as any).data_preview).length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-muted-foreground">
                        {Object.entries((step as any).data_preview as Record<string, unknown>).map(([pk, pv]) => {
                          if (pk === "status" && typeof pv === "string") return <span key={pk}>Status: <strong className="text-foreground">{pv}</strong></span>;
                          if (pk === "total_adsets" && typeof pv === "number") return <span key={pk}>Adsets: <strong className="text-foreground">{pv}</strong></span>;
                          if (pk === "unique_domains" && typeof pv === "number") return <span key={pk}>Dom\u00ednios: <strong className="text-foreground">{pv}</strong></span>;
                          if (pk === "total_spend" && typeof pv === "number") return <span key={pk}>Spend: <strong className="text-foreground">R${(pv as number).toFixed(2)}</strong></span>;
                          if (pk === "total_revenue" && typeof pv === "number") return <span key={pk}>Receita: <strong className="text-foreground">R${(pv as number).toFixed(2)}</strong></span>;
                          if (pk === "total_profit" && typeof pv === "number") return <span key={pk}>Lucro: <strong className={`${(pv as number) >= 0 ? "text-green-500" : "text-red-500"}`}>R${(pv as number).toFixed(2)}</strong></span>;
                          if (pk === "overall_roi" && typeof pv === "number") return <span key={pk}>ROI: <strong className={`${(pv as number) >= 0 ? "text-green-500" : "text-red-500"}`}>{(pv as number).toFixed(1)}%</strong></span>;
                          if (pk === "thresholds" && typeof pv === "object" && pv) {
                            const th = pv as Record<string, unknown>;
                            return <span key={pk}>Stoploss: <strong className="text-foreground">{String(th.stoploss_cpa ?? "?")}</strong> | Scale: <strong className="text-foreground">{String(th.scale_cpa ?? "?")}</strong></span>;
                          }
                          if (pk === "activation" && typeof pv === "object" && pv) {
                            const act = pv as Record<string, unknown>;
                            return <span key={pk}>Hora: <strong className="text-foreground">{String(act.optimal_hour ?? "?")}:00</strong></span>;
                          }
                          if (pk === "action_distribution" && typeof pv === "object" && pv) {
                            const ad = pv as Record<string, number>;
                            const sorted = Object.entries(ad).sort((a, b) => b[1] - a[1]);
                            return <span key={pk}>Decis\u00f5es: <strong className="text-foreground">{sorted.map(([k,v]) => `${k}:${v}`).join(", ")}</strong></span>;
                          }
                          if (pk === "domain_summaries" && typeof pv === "object" && pv) {
                            const ds = pv as Record<string, Record<string, unknown>>;
                            return <span key={pk} className="block w-full mt-0.5">
                              {Object.entries(ds).map(([dn, dd]) => {
                                const roi = typeof dd.site_roi === "number" ? `ROI=${dd.site_roi.toFixed(1)}%` : "";
                                const spend = typeof dd.site_spend === "number" ? `Spend=R$${dd.site_spend.toFixed(2)}` : "";
                                const receita = typeof dd.site_receita === "number" ? `Receita=R$${dd.site_receita.toFixed(2)}` : "";
                                return <span key={dn} className="block"><strong className="text-foreground">{dn}</strong>: {[roi, spend, receita].filter(Boolean).join(" | ")}</span>;
                              })}
                            </span>;
                          }
                          if (typeof pv === "string" || typeof pv === "number" || typeof pv === "boolean") {
                            return <span key={pk}>{pk}: <strong className="text-foreground">{String(pv)}</strong></span>;
                          }
                          return null;
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Extra/unknown fields as raw JSON */}
      {extraFields.length > 0 && (
        <div>
          <span className={`text-muted-foreground block mb-1 uppercase tracking-wider ${labelSize}`}>
            Dados Adicionais
          </span>
          <pre className={`font-mono ${textSize} text-muted-foreground bg-background/50 rounded-lg p-2 overflow-x-auto border border-border/20 max-h-32`}>
            {JSON.stringify(Object.fromEntries(extraFields), null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

// ─── Log Row (List View) ─────────────────────────────────────────────────────

interface LogEntry {ber;
  category: string;
  action: string;
  severity: string;
  strategy_tag: string | null;
  campaign_id: string | null;
  adset_id: string | null;
  agent: string;
  reason: string | null;
  details_json: Record<string, unknown> | null;
  result: string | null;
  created_at: string;
}

function LogRow({ log }: { log: LogEntry }) {
  const [expanded, setExpanded] = useState(false);
  const agentConfig = getAgentConfig(log.agent);
  const businessSummary = log.details_json?.business_summary as BusinessSummary | undefined;
  const hasBusinessData = businessSummary && businessSummary.highlights && businessSummary.highlights.length > 0;

  // Build a short inline summary for list view
  const inlineSummary = hasBusinessData && businessSummary.executive_summary
    ? businessSummary.executive_summary
    : log.reason || "\u2014";

  return (
    <div
      className="border-b border-border/50 last:border-b-0 hover:bg-card/80 transition-all cursor-pointer group"
      onClick={() => setExpanded(!expanded)}
    >
      <div className="flex items-center gap-3 px-4 py-3">
        {/* Agent color indicator */}
        <div
          className="w-1 h-8 rounded-full shrink-0 transition-all group-hover:h-10"
          style={{ backgroundColor: agentConfig.color }}
        />

        {/* Timestamp */}
        <div className="flex flex-col items-end min-w-[60px]" title={formatDate(log.created_at)}>
          <span className="text-xs font-mono text-muted-foreground">{relativeTime(log.created_at)}</span>
        </div>

        {/* Severity dot */}
        <SeverityBadge severity={log.severity} />

        {/* Category */}
        <CategoryBadge category={log.category} />

        {/* Action */}
        <span className="text-sm font-medium text-foreground truncate min-w-[120px]">
          {actionLabels[log.action] || log.action}
        </span>

        {/* Reason / Executive Summary */}
        <span className="text-sm text-muted-foreground truncate flex-1">
          {inlineSummary}
        </span>

        {/* Business data indicator */}
        {hasBusinessData && (
          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20 font-medium shrink-0">
            {businessSummary.data_status === "no_data" ? "Sem dados" : businessSummary.domain || "Detalhes"}
          </span>
        )}

        {/* Result */}
        <ResultBadge result={log.result} />

        {/* Agent badge */}
        <div className="hidden lg:block">
          <AgentBadge agent={log.agent} />
        </div>

        {/* Expand indicator */}
        <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground/50 transition-transform ${expanded ? "rotate-180" : ""}`} />
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="px-4 pb-3 pt-0 animate-fade-in">
          <div
            className="rounded-xl p-4 border grid grid-cols-2 md:grid-cols-4 gap-4 text-xs"
            style={{
              backgroundColor: `${agentConfig.color}08`,
              borderColor: `${agentConfig.color}20`,
            }}
          >
            <div>
              <span className="text-muted-foreground block mb-1 uppercase tracking-wider text-[10px]">Data/Hora</span>
              <span className="text-foreground font-mono">{formatDate(log.created_at)}</span>
            </div>
            <div>
              <span className="text-muted-foreground block mb-1 uppercase tracking-wider text-[10px]">Agente</span>
              <AgentBadge agent={log.agent} />
            </div>
            {log.campaign_id && (
              <div>
                <span className="text-muted-foreground block mb-1 uppercase tracking-wider text-[10px]">Campaign ID</span>
                <span className="text-foreground font-mono text-[11px]">{log.campaign_id}</span>
              </div>
            )}
            {log.adset_id && (
              <div>
                <span className="text-muted-foreground block mb-1 uppercase tracking-wider text-[10px]">Adset ID</span>
                <span className="text-foreground font-mono text-[11px]">{log.adset_id}</span>
              </div>
            )}
            {log.strategy_tag && (
              <div>
                <span className="text-muted-foreground block mb-1 uppercase tracking-wider text-[10px]">Strategy</span>
                <span className="text-foreground font-medium">{log.strategy_tag}</span>
              </div>
            )}
            {/* Business Summary Card (rich view) */}
            {hasBusinessData && (
              <div className="col-span-2 md:col-span-4">
                <span className="text-muted-foreground block mb-1.5 uppercase tracking-wider text-[10px]">Resumo de Neg\u00f3cio</span>
                <BusinessSummaryCard summary={businessSummary} agentColor={agentConfig.color} />
              </div>
            )}
            {log.details_json && (
              <div className="col-span-2 md:col-span-4">
                <span className="text-muted-foreground block mb-1.5 uppercase tracking-wider text-[10px]">Detalhes T\u00e9cnicos</span>
                <DetailsPanelRich details={log.details_json} agentColor={agentConfig.color} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Kanban View (Process Lifecycle) ─────────────────────────

const statusIcons: Record<string, typeof Activity> = {
  planned: ClipboardList,
  in_progress: Activity,
  review: Eye,
  done: CheckCircle2,
};

function KanbanView() {
  const { data, isLoading, isError, refetch } = trpc.brain.getLogsProcessKanban.useQuery(
    { period_hours: 168 },
    { refetchInterval: 60_000, retry: false }
  );

  if (isLoading) {
    return (
      <div className="flex gap-4 overflow-x-auto pb-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="min-w-[280px] flex-1 bg-card border border-border rounded-xl p-4 animate-pulse">
            <div className="h-6 w-32 bg-border rounded mb-4" />
            <div className="space-y-3">
              {[...Array(3)].map((_, j) => (
                <div key={j} className="h-20 bg-border/50 rounded-lg" />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <AlertTriangle className="w-8 h-8 text-amber-400" />
        <p className="text-sm text-muted-foreground">Erro ao carregar Kanban</p>
        <button onClick={() => refetch()} className="text-xs text-primary hover:underline">
          Tentar novamente
        </button>
      </div>
    );
  }

  if (!data || !data.columns || Object.keys(data.columns).length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <LayoutGrid className="w-8 h-8 text-muted-foreground/50" />
        <p className="text-sm text-muted-foreground">Nenhuma atividade nos últimos 7 dias</p>
      </div>
    );
  }

  const columnOrder = ["planned", "in_progress", "review", "done"];
  const totalLogs = data.summary?.total || 0;

  return (
    <div className="space-y-4">
      {/* Summary bar */}
      {totalLogs > 0 && (
        <div className="flex items-center gap-3 bg-card border border-border rounded-xl px-4 py-3">
          <span className="text-sm font-medium text-foreground">{totalLogs} ações nos últimos 7 dias</span>
          <div className="flex-1 flex items-center gap-1 h-2 bg-border/30 rounded-full overflow-hidden">
            {columnOrder.map((key) => {
              const col = data.columns[key];
              if (!col || col.logs.length === 0) return null;
              const pct = (col.logs.length / totalLogs) * 100;
              return (
                <div
                  key={key}
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${pct}%`, backgroundColor: col.color, minWidth: pct > 0 ? "4px" : "0" }}
                  title={`${col.title}: ${col.logs.length}`}
                />
              );
            })}
          </div>
          <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
            {columnOrder.map((key) => {
              const col = data.columns[key];
              if (!col) return null;
              return (
                <span key={key} className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: col.color }} />
                  {col.logs.length}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Kanban columns */}
      <div className="flex gap-4 overflow-x-auto pb-4 -mx-2 px-2">
        {columnOrder.map((key) => {
          const col = data.columns[key];
          if (!col) return null;
          return (
            <ProcessKanbanColumn
              key={key}
              statusKey={key}
              column={col}
              agentColors={data.agent_colors || {}}
            />
          );
        })}
      </div>
    </div>
  );
}

interface ProcessKanbanColumnProps {
  statusKey: string;
  column: {
    title: string;
    color: string;
    icon: string;
    logs: Array<LogEntry & { agent: string }>;
  };
  agentColors: Record<string, { color: string; label: string }>;
}

function ProcessKanbanColumn({ statusKey, column, agentColors }: ProcessKanbanColumnProps) {
  const [collapsed, setCollapsed] = useState(false);
  const StatusIcon = statusIcons[statusKey] || Activity;

  return (
    <div
      className="min-w-[280px] flex-1 bg-card border rounded-xl overflow-hidden transition-all duration-300 hover:shadow-lg flex flex-col"
      style={{ borderColor: `${column.color}30` }}
    >
      {/* Column Header */}
      <div
        className="px-4 py-3 border-b flex items-center justify-between cursor-pointer"
        style={{ borderColor: `${column.color}20`, background: `linear-gradient(135deg, ${column.color}10, transparent)` }}
        onClick={() => setCollapsed(!collapsed)}
      >
        <div className="flex items-center gap-2.5">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: `${column.color}20` }}
          >
            <StatusIcon className="w-4 h-4" style={{ color: column.color }} />
          </div>
          <div>
            <span className="text-sm font-bold text-foreground block">{column.title}</span>
            <span className="text-[10px] text-muted-foreground">
              {column.logs.length} {column.logs.length === 1 ? "item" : "itens"}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="text-xs font-bold px-2 py-1 rounded-full"
            style={{ color: column.color, backgroundColor: `${column.color}15` }}
          >
            {column.logs.length}
          </span>
          {collapsed ? <Eye className="w-3.5 h-3.5 text-muted-foreground" /> : <EyeOff className="w-3.5 h-3.5 text-muted-foreground" />}
        </div>
      </div>

      {/* Cards */}
      {!collapsed && (
        <div className="p-3 space-y-2 overflow-y-auto max-h-[500px] flex-1">
          {column.logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground/50">
              <StatusIcon className="w-6 h-6 mb-2" />
              <span className="text-xs">Nenhum item</span>
            </div>
          ) : (
            column.logs.map((log) => (
              <ProcessKanbanCard
                key={log.id}
                log={log}
                columnColor={column.color}
                agentColors={agentColors}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ─── Business Summary Card ──────────────────────────────────────────────────

interface BusinessHighlight {
  label: string;
  value: string;
  type: "metric" | "money" | "actions" | "status";
  trend?: "up" | "down";
  status?: "success" | "warning" | "error";
}

interface BusinessAction {
  type: string;
  target: string;
  detail: string;
  reason: string;
}

interface StepDetail {
  step: string;
  strategy?: string;
  status: string;
  data_status?: string;
  total_adsets?: number;
  stoploss_cpa?: number;
  scale_cpa?: number;
  optimal_hour?: number;
  confidence?: number;
  config_pushed?: string;
}

interface BusinessSummary {
  title: string;
  domain: string | null;
  highlights: BusinessHighlight[];
  actions_taken: BusinessAction[];
  step_details?: StepDetail[];
  executive_summary?: string;
  data_status: "active" | "no_data" | "error";
}

const highlightTypeIcons: Record<string, React.ElementType> = {
  metric: BarChart3,
  money: DollarSign,
  actions: Target,
  status: Activity,
};

const actionTypeColors: Record<string, { bg: string; text: string; border: string }> = {
  SCALE_UP: { bg: "rgba(34,197,94,0.1)", text: "#22c55e", border: "rgba(34,197,94,0.25)" },
  MAINTAIN: { bg: "rgba(59,130,246,0.1)", text: "#3b82f6", border: "rgba(59,130,246,0.25)" },
  OPTIMIZE: { bg: "rgba(245,158,11,0.1)", text: "#f59e0b", border: "rgba(245,158,11,0.25)" },
  SCALE_DOWN: { bg: "rgba(249,115,22,0.1)", text: "#f97316", border: "rgba(249,115,22,0.25)" },
  REDUCE: { bg: "rgba(249,115,22,0.1)", text: "#f97316", border: "rgba(249,115,22,0.25)" },
  PAUSE: { bg: "rgba(239,68,68,0.1)", text: "#ef4444", border: "rgba(239,68,68,0.25)" },
  MONITOR: { bg: "rgba(148,163,184,0.1)", text: "#94a3b8", border: "rgba(148,163,184,0.25)" },
};

function BusinessSummaryCard({ summary, agentColor }: { summary: BusinessSummary; agentColor: string }) {
  const [showActions, setShowActions] = useState(false);
  const [showSteps, setShowSteps] = useState(false);

  return (
    <div className="space-y-2.5">
      {/* Title with domain */}
      <div className="flex items-center gap-2">
        <div
          className="w-6 h-6 rounded-md flex items-center justify-center shrink-0"
          style={{ backgroundColor: `${agentColor}20` }}
        >
          <Globe className="w-3.5 h-3.5" style={{ color: agentColor }} />
        </div>
        <div className="min-w-0">
          <span className="text-[11px] font-bold text-foreground block truncate">
            {summary.title}
          </span>
          {summary.domain && (
            <span className="text-[9px] text-muted-foreground font-mono">{summary.domain}</span>
          )}
        </div>
        {summary.data_status === "no_data" && (
          <span className="ml-auto text-[9px] px-1.5 py-0.5 rounded-full bg-yellow-500/10 text-yellow-500 border border-yellow-500/20 font-medium shrink-0">
            Sem dados
          </span>
        )}
        {summary.data_status === "active" && (
          <span className="ml-auto text-[9px] px-1.5 py-0.5 rounded-full bg-green-500/10 text-green-500 border border-green-500/20 font-medium shrink-0">
            Ativo
          </span>
        )}
      </div>

      {/* Executive Summary */}
      {summary.executive_summary && (
        <div
          className="rounded-md px-2.5 py-2 border text-[10px] leading-relaxed text-foreground/80"
          style={{ backgroundColor: `${agentColor}06`, borderColor: `${agentColor}15` }}
        >
          <div className="flex items-center gap-1 mb-1">
            <Info className="w-3 h-3 text-muted-foreground" />
            <span className="text-[9px] text-muted-foreground uppercase tracking-wider font-medium">Resumo</span>
          </div>
          {summary.executive_summary}
        </div>
      )}

      {/* Highlights grid */}
      {summary.highlights.length > 0 && (
        <div className="grid grid-cols-2 gap-1.5">
          {summary.highlights.map((h, i) => {
            const Icon = highlightTypeIcons[h.type] || Activity;
            const trendColor = h.trend === "up" ? "#22c55e" : h.trend === "down" ? "#ef4444" : undefined;
            const statusColor = h.status === "success" ? "#22c55e" : h.status === "error" ? "#ef4444" : h.status === "warning" ? "#f59e0b" : undefined;
            const valueColor = trendColor || statusColor;

            return (
              <div
                key={i}
                className="rounded-md px-2 py-1.5 border border-border/30 bg-background/50"
              >
                <div className="flex items-center gap-1 mb-0.5">
                  <Icon className="w-2.5 h-2.5 text-muted-foreground" />
                  <span className="text-[9px] text-muted-foreground uppercase tracking-wider truncate">
                    {h.label}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <span
                    className="text-[11px] font-bold truncate"
                    style={{ color: valueColor || "inherit" }}
                  >
                    {h.value}
                  </span>
                  {h.trend === "up" && <ArrowUpRight className="w-3 h-3 text-green-500 shrink-0" />}
                  {h.trend === "down" && <ArrowDownRight className="w-3 h-3 text-red-500 shrink-0" />}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Step Details (business-level per step) */}
      {summary.step_details && summary.step_details.length > 0 && (
        <div>
          <button
            className="flex items-center gap-1 text-[9px] text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors mb-1"
            onClick={(e) => {
              e.stopPropagation();
              setShowSteps(!showSteps);
            }}
          >
            <Settings2 className="w-3 h-3" />
            {summary.step_details.length} etapa{summary.step_details.length === 1 ? "" : "s"} executada{summary.step_details.length === 1 ? "" : "s"}
            <ChevronDown className={`w-3 h-3 transition-transform ${showSteps ? "rotate-180" : ""}`} />
          </button>
          {showSteps && (
            <div className="space-y-1 animate-in fade-in slide-in-from-top-1 duration-200">
              {summary.step_details.map((sd, i) => {
                const isOk = sd.status === "success" || sd.status === "completed" || sd.status === "?";
                return (
                  <div
                    key={i}
                    className="rounded-md px-2 py-1.5 border text-[10px]"
                    style={{
                      backgroundColor: isOk ? `${agentColor}06` : "rgba(239,68,68,0.06)",
                      borderColor: isOk ? `${agentColor}15` : "rgba(239,68,68,0.15)",
                    }}
                  >
                    <div className="flex items-center gap-1.5 mb-0.5">
                      {isOk ? <CheckCircle2 className="w-3 h-3 text-green-500 shrink-0" /> : <XCircle className="w-3 h-3 text-red-500 shrink-0" />}
                      <span className="font-semibold text-foreground">{sd.step}</span>
                      {sd.strategy && (
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20">
                          {sd.strategy}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-muted-foreground">
                      {sd.total_adsets !== undefined && <span>Adsets: <strong className="text-foreground">{sd.total_adsets}</strong></span>}
                      {sd.data_status && <span>Status: <strong className="text-foreground">{sd.data_status}</strong></span>}
                      {sd.stoploss_cpa !== undefined && <span>Stoploss: <strong className="text-foreground">R${sd.stoploss_cpa}</strong></span>}
                      {sd.scale_cpa !== undefined && <span>Scale: <strong className="text-foreground">R${sd.scale_cpa}</strong></span>}
                      {sd.optimal_hour !== undefined && <span>Hora: <strong className="text-foreground">{sd.optimal_hour}:00</strong> ({sd.confidence !== undefined ? `${Math.round((sd.confidence || 0) * 100)}%` : "?"})</span>}
                      {sd.config_pushed && <span>Push: <strong className="text-foreground">{sd.config_pushed}</strong></span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Actions taken by domain */}
      {summary.actions_taken.length > 0 && (
        <div>
          <button
            className="flex items-center gap-1 text-[9px] text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors mb-1"
            onClick={(e) => {
              e.stopPropagation();
              setShowActions(!showActions);
            }}
          >
            <Layers className="w-3 h-3" />
            {summary.actions_taken.length} a\u00e7{summary.actions_taken.length === 1 ? "\u00e3o" : "\u00f5es"} por dom\u00ednio
            <ChevronDown className={`w-3 h-3 transition-transform ${showActions ? "rotate-180" : ""}`} />
          </button>
          {showActions && (
            <div className="space-y-1 animate-in fade-in slide-in-from-top-1 duration-200">
              {summary.actions_taken.map((action, i) => {
                const colors = actionTypeColors[action.type] || actionTypeColors.MONITOR;
                return (
                  <div
                    key={i}
                    className="rounded-md px-2 py-1.5 border text-[10px]"
                    style={{ backgroundColor: colors.bg, borderColor: colors.border }}
                  >
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span
                        className="px-1.5 py-0.5 rounded text-[9px] font-bold"
                        style={{ color: colors.text, backgroundColor: `${colors.text}15` }}
                      >
                        {action.type}
                      </span>
                      <span className="text-foreground font-medium truncate">{action.target}</span>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">{action.detail}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Process Kanban Card ────────────────────────────────────────────────────

function ProcessKanbanCard({
  log,
  columnColor,
  agentColors,
}: {
  log: LogEntry & { agent: string };
  columnColor: string;
  agentColors: Record<string, { color: string; label: string }>;
}) {
  const [expanded, setExpanded] = useState(false);
  const agentInfo = agentColors[log.agent] || getAgentConfig(log.agent);
  const businessSummary = log.details_json?.business_summary as BusinessSummary | undefined;
  const hasBusinessData = businessSummary && businessSummary.highlights && businessSummary.highlights.length > 0;

  return (
    <div
      className="bg-background/80 backdrop-blur-sm border rounded-lg p-3 cursor-pointer transition-all duration-200 hover:shadow-md hover:translate-y-[-1px]"
      style={{ borderColor: hasBusinessData ? `${agentInfo.color}25` : `${columnColor}15` }}
      onClick={() => setExpanded(!expanded)}
    >
      {/* Header: action + result */}
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <span className="text-xs font-semibold text-foreground leading-tight">
          {actionLabels[log.action] || log.action}
        </span>
        <ResultBadge result={log.result} />
      </div>

      {/* Business Summary (inline preview — always visible when available) */}
      {hasBusinessData && (
        <div className="mb-2">
          <BusinessSummaryCard summary={businessSummary} agentColor={agentInfo.color} />
        </div>
      )}

      {/* Reason (only show if no business summary) */}
      {!hasBusinessData && log.reason && (
        <p className="text-[11px] text-muted-foreground leading-relaxed line-clamp-2 mb-2">
          {log.reason}
        </p>
      )}

      {/* Footer: agent + category + time */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {/* Agent indicator */}
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold border"
            style={{
              color: agentInfo.color,
              backgroundColor: `${agentInfo.color}12`,
              borderColor: `${agentInfo.color}25`,
            }}
          >
            <Bot className="w-2.5 h-2.5" />
            {agentInfo.label}
          </span>
          <CategoryBadge category={log.category} />
        </div>
        <span className="text-[10px] text-muted-foreground font-mono flex items-center gap-1 shrink-0">
          <Clock className="w-3 h-3" />
          {relativeTime(log.created_at)}
        </span>
      </div>

      {/* Expanded details (technical) */}
      {expanded && log.details_json && (
        <div className="mt-2 pt-2 border-t border-border/30 animate-fade-in">
          <DetailsPanelRich details={log.details_json} agentColor={agentInfo.color} compact />
        </div>
      )}
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────

export function Logs() {
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [filters, setFilters] = useState<LogsFilter>({
    limit: 50,
    offset: 0,
  });

  const { data, isLoading, isError, refetch } = trpc.brain.getLogs.useQuery(
    filters,
    { refetchInterval: 30_000, retry: false, placeholderData: (prev) => prev }
  );

  const handleFilterChange = (partial: Partial<LogsFilter>) => {
    setFilters((prev) => ({ ...prev, ...partial }));
  };

  const resetFilters = () => {
    setFilters({ limit: 50, offset: 0 });
  };

  const totalPages = data ? Math.ceil(data.total / filters.limit) : 0;
  const currentPage = Math.floor(filters.offset / filters.limit) + 1;

  return (
    <div className="space-y-6 py-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg brand-gradient flex items-center justify-center">
              <ScrollText className="w-4.5 h-4.5 text-white" />
            </div>
            Action Logs
          </h1>
          <p className="text-sm text-muted-foreground mt-1.5">
            Histórico de todas as ações executadas pela Megan AI em campanhas do Facebook
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground bg-card border border-border rounded-xl hover:shadow-lg hover:border-primary/30 transition-all duration-200"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
          Atualizar
        </button>
      </div>

      {/* Stats */}
      <StatsCards />

      {/* Filters + View Toggle */}
      <FilterBar
        filters={filters}
        onFilterChange={handleFilterChange}
        onReset={resetFilters}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      {/* Content */}
      {viewMode === "backlog" ? (
        <BacklogView />
      ) : viewMode === "kanban" ? (
        <KanbanView />
      ) : (
        <>
          {/* Logs Table */}
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            {/* Table Header */}
            <div className="flex items-center gap-3 px-4 py-3 bg-card border-b border-border text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
              <span className="w-1" />
              <span className="min-w-[60px] text-right">Quando</span>
              <span className="min-w-[70px]">Sev.</span>
              <span className="min-w-[110px]">Categoria</span>
              <span className="min-w-[120px]">Ação</span>
              <span className="flex-1">Motivo</span>
              <span className="min-w-[80px]">Status</span>
              <span className="min-w-[150px] text-right hidden lg:block">Agente</span>
              <span className="w-4" />
            </div>

            {/* Loading */}
            {isLoading && !data && (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 text-primary animate-spin" />
                <span className="ml-3 text-sm text-muted-foreground">Carregando logs...</span>
              </div>
            )}

            {/* Error */}
            {isError && (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <AlertTriangle className="w-8 h-8 text-amber-400" />
                <p className="text-sm text-muted-foreground">Erro ao carregar logs</p>
                <button
                  onClick={() => refetch()}
                  className="text-xs text-primary hover:underline"
                >
                  Tentar novamente
                </button>
              </div>
            )}

            {/* Empty */}
            {data && data.logs.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <ScrollText className="w-8 h-8 text-muted-foreground/50" />
                <p className="text-sm text-muted-foreground">Nenhum log encontrado</p>
                <p className="text-xs text-muted-foreground/70">
                  Os logs aparecerão aqui quando a Megan AI começar a operar campanhas com a tag [megan-ai]
                </p>
              </div>
            )}

            {/* Rows */}
            {data && data.logs.length > 0 && (
              <div>
                {data.logs.map((log) => (
                  <LogRow key={log.id} log={log as LogEntry} />
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          {data && data.total > filters.limit && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                {data.total} logs encontrados — Página {currentPage} de {totalPages}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleFilterChange({ offset: Math.max(0, filters.offset - filters.limit) })}
                  disabled={filters.offset === 0}
                  className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground bg-card border border-border rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-all hover:shadow-md"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Anterior
                </button>
                <button
                  onClick={() => handleFilterChange({ offset: filters.offset + filters.limit })}
                  disabled={!data.has_more}
                  className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground bg-card border border-border rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-all hover:shadow-md"
                >
                  Próxima
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
